import { useState } from 'react';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  loginThunk,
  setOnboardingComplete,
  socialLoginThunk,
} from '../redux/slices/authSlice';
import SignUpImage from '../assets/Signup.svg';
import Logo from '../assets/Logo.svg';
import { toast } from 'react-hot-toast';
import { Eye, EyeOff } from 'lucide-react';
import GoogleImage from '../assets/Google.svg';
import FacebookImage from '../assets/Facebook.svg';
import AppleImage from '../assets/Apple.svg';
import { useCountry } from '../helpers/useCountry';
import { signInWithPopup } from 'firebase/auth';
import {
  auth,
  googleProvider,
  // facebookProvider,
  appleProvider,
} from '../firebase';
import { containerAnim, staggerFadeUp } from '../helpers/animation';
import FooterAuth from '../components/layout/FooterAuth';
import { useUpgradeIntent } from '../helpers/useUpgradeIntent';

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { country, region } = useCountry();
  const [showPassword, setShowPassword] = useState(false);
  const [socialLoading, setSocialLoading] = useState(false);
  const { loading } = useSelector((state) => state?.auth);
  const rawRedirect = searchParams.get('redirect');
  const redirectTo =
    rawRedirect && rawRedirect.startsWith('/app') ? rawRedirect : '/app';
  useUpgradeIntent();
  // ✉️ Email/password login
  const handleSubmit = (e) => {
    e.preventDefault();
    const email = e.target.email.value.trim().toLowerCase();
    const password = e.target.password.value.trim();

    let message = '';

    if (!email && !password) {
      message = 'Email and password are required.';
    } else if (!email) {
      message = 'Email is required.';
    } else if (!password) {
      message = 'Password is required.';
    }

    if (message) {
      toast.error(message);
      return;
    }

    dispatch(
      loginThunk({
        email,
        password,
        country,
        session_state: region,
      }),
    )
      .unwrap()
      .then(() => {
        dispatch(setOnboardingComplete());
        toast.success('Logged in successfully!');
        navigate(redirectTo, { replace: true });
      })
      .catch((err) => toast.error(err));
  };

  // 🌐 Social login (popup only)
  const handleSocialLogin = async (providerName) => {
    if (socialLoading) return; // prevent double clicks
    setSocialLoading(true);

    try {
      let provider;
      switch (providerName) {
        case 'Google':
          provider = googleProvider;
          break;
        // case 'Facebook':
        //   provider = facebookProvider;
        //   break;
        case 'Apple':
          provider = appleProvider;
          break;
        default:
          toast.error('Unsupported provider');
          return;
      }

      localStorage.setItem('authProvider', providerName.toLowerCase());
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const idToken = await user.getIdToken();

      await dispatch(
        socialLoginThunk({
          provider: providerName.toLowerCase(),
          idToken,
          country,
          session_state: region,
        }),
      ).unwrap();

      dispatch(setOnboardingComplete());
      toast.success(`Welcome  ${user.displayName || 'user'} 🎉`);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      console.error(`${providerName} login error:`, error);
      if (error.code === 'auth/popup-closed-by-user') {
        toast('Login canceled by user', { icon: '⚠️' });
      } else {
        toast.error(`${providerName} login failed`);
      }
    } finally {
      setSocialLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen w-full font-sans bg-gray-50">
      {socialLoading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center"
          >
            <div className="loader border-t-4 border-brand border-solid rounded-full w-12 h-12 animate-spin mb-3"></div>
            <p className="font-semibold text-gray-700">
              Connecting to your account...
            </p>
          </motion.div>
        </div>
      )}
      <div className="flex flex-col md:flex-row flex-grow">
        <motion.div
          {...containerAnim}
          className="w-full md:w-1/2 flex flex-col px-6 md:px-16 py-8 md:py-10 bg-white shadow-lg"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="absolute top-6 left-0 right-0 mx-auto w-fit md:left-10 md:right-auto md:mx-0"
          >
            <a href="/">
              <img
                src={Logo}
                alt="Legal Intelligence Logo"
                className="w-20 h-auto"
              />
            </a>
          </motion.div>

          <div className="w-full max-w-md md:pt-16 mx-auto">
            <motion.h1
              {...staggerFadeUp(0.3)}
              className="font-dmsans text-4xl font-bold text-gray-900 mb-3 text-center mt-8 md:mt-0"
            >
              Log In
            </motion.h1>

            <motion.p
              {...staggerFadeUp(0.4)}
              className=" text-md mb-8 text-center font-bold"
            >
              Access your legal workspace.
            </motion.p>

            {/* Email login */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                name="email"
                placeholder="Email"
                className="w-full border-2 border-gray-600 rounded-lg px-4 py-2 focus:outline-none
               placeholder:font-serif placeholder:text-gray-500 placeholder:text-base"
              />

              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  className="w-full border-2 border-gray-600 rounded-lg px-4 py-2 pr-10 focus:outline-none
                 placeholder:font-serif placeholder:text-gray-500 placeholder:text-base"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-brandDark to-brand text-white py-2 rounded-lg shadow-md hover:brightness-105 transition-all"
              >
                {loading ? 'Logging in...' : 'Log In'}
              </motion.button>
            </form>

            <div className="my-5 flex items-center">
              <div className="flex-grow border-t border-brand"></div>
              <div className="flex-grow border-t border-brand"></div>
            </div>
            <div className="space-y-3 mt-5">
              {['Google', 'Apple'].map((provider) => (
                <motion.button
                  key={provider}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleSocialLogin(provider)}
                  className="w-full flex items-center justify-center border border-brand py-2 rounded text-brand hover:bg-gray-100 transition"
                >
                  <img
                    src={
                      provider === 'Google'
                        ? GoogleImage
                        : // : provider === 'Facebook'
                          // ? FacebookImage
                          AppleImage
                    }
                    alt={provider}
                    className="w-6 h-6 mr-2"
                  />
                  Log in with {provider}
                </motion.button>
              ))}
            </div>

            <p className="mt-6 text-sm font-semibold text-gray-600 underline text-center">
              <Link to="/forgot-password"> Forgot your password? </Link>
            </p>
            <p className="mt-2 text-sm font-semibold text-gray-600 text-center">
              Don’t have an account?{' '}
              <Link
                to="/signup"
                className="text-black underline font-semibold hover:underline"
              >
                Sign Up
              </Link>
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="w-full md:w-1/2 flex items-center justify-center bg-[#DADDFB] overflow-hidden"
        >
          <img
            src={SignUpImage}
            alt="Login Illustration"
            className="max-w-[80%] max-h-[80%] object-contain"
            loading="lazy"
          />
        </motion.div>
      </div>
      <FooterAuth />
    </div>
  );
}
